import React, { useState, useReducer } from 'react';
import { mockLoans, Loan, ProcessedLoan } from '../data/mockLoans';
import { HistorySidebar } from '../components/HistorySidebar';
import { StartScreen } from '../components/StartScreen';
import { QueueEmptyState } from '../components/QueueEmptyState';
import { ActiveLoanCard } from '../components/ActiveLoanCard';
import { CallDispositionModal } from '../components/CallDispositionModal';
import { NoteModal } from '../components/NoteModal';
import { SnoozeModal } from '../components/SnoozeModal';
import { AnimatePresence } from 'framer-motion';
import { Clock } from 'lucide-react';
type ActionType = 'calling' | 'noting' | 'snoozing' | null;
interface QueueState {
  status: 'idle' | 'active' | 'complete';
  queue: Loan[];
  currentLoan: Loan | null;
  history: ProcessedLoan[];
  currentAction: ActionType;
}
type QueueAction = {
  type: 'START_QUEUE';
} | {
  type: 'SET_ACTION';
  payload: ActionType;
} | {
  type: 'COMPLETE_CALL';
  payload: {
    disposition: 'contacted' | 'no_answer' | 'voicemail';
    note: string;
    followUpTime: string;
  };
} | {
  type: 'LEAVE_NOTE';
  payload: {
    note: string;
  };
} | {
  type: 'SNOOZE_LOAN';
  payload: {
    snoozeUntil: string;
  };
};
const initialState: QueueState = {
  status: 'idle',
  queue: mockLoans,
  currentLoan: null,
  history: [],
  currentAction: null
};
function queueReducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case 'START_QUEUE':
      return {
        ...state,
        status: 'active',
        currentLoan: state.queue[0] || null
      };
    case 'SET_ACTION':
      return {
        ...state,
        currentAction: action.payload
      };
    case 'COMPLETE_CALL':
      {
        if (!state.currentLoan) return state;
        const processed: ProcessedLoan = {
          loan: state.currentLoan,
          action: 'called',
          disposition: action.payload.disposition,
          note: action.payload.note,
          followUpTime: action.payload.followUpTime || undefined,
          processedAt: new Date().toISOString()
        };
        const nextQueue = state.queue.slice(1);
        return {
          ...state,
          history: [processed, ...state.history],
          queue: nextQueue,
          currentLoan: nextQueue[0] || null,
          status: nextQueue.length === 0 ? 'complete' : 'active',
          currentAction: null
        };
      }
    case 'LEAVE_NOTE':
      {
        if (!state.currentLoan) return state;
        const processed: ProcessedLoan = {
          loan: state.currentLoan,
          action: 'noted',
          note: action.payload.note,
          processedAt: new Date().toISOString()
        };
        const nextQueue = state.queue.slice(1);
        return {
          ...state,
          history: [processed, ...state.history],
          queue: nextQueue,
          currentLoan: nextQueue[0] || null,
          status: nextQueue.length === 0 ? 'complete' : 'active',
          currentAction: null
        };
      }
    case 'SNOOZE_LOAN':
      {
        if (!state.currentLoan) return state;
        const processed: ProcessedLoan = {
          loan: state.currentLoan,
          action: 'snoozed',
          snoozeUntil: action.payload.snoozeUntil,
          processedAt: new Date().toISOString()
        };
        const nextQueue = state.queue.slice(1);
        return {
          ...state,
          history: [processed, ...state.history],
          queue: nextQueue,
          currentLoan: nextQueue[0] || null,
          status: nextQueue.length === 0 ? 'complete' : 'active',
          currentAction: null
        };
      }
    default:
      return state;
  }
}
export function ProcessorCallList() {
  const [state, dispatch] = useReducer(queueReducer, initialState);
  const [historyOpen, setHistoryOpen] = useState(false);
  const handleStart = () => dispatch({
    type: 'START_QUEUE'
  });
  const handleActionClick = (action: ActionType) => {
    dispatch({
      type: 'SET_ACTION',
      payload: action
    });
  };
  const handleCallSubmit = (disposition: 'contacted' | 'no_answer' | 'voicemail', note: string, followUpTime: string) => {
    dispatch({
      type: 'COMPLETE_CALL',
      payload: {
        disposition,
        note,
        followUpTime
      }
    });
  };
  const handleNoteSubmit = (note: string) => {
    dispatch({
      type: 'LEAVE_NOTE',
      payload: {
        note
      }
    });
  };
  const handleSnoozeSubmit = (snoozeUntil: string) => {
    dispatch({
      type: 'SNOOZE_LOAN',
      payload: {
        snoozeUntil
      }
    });
  };
  const handleCloseModal = () => {
    dispatch({
      type: 'SET_ACTION',
      payload: null
    });
  };
  return <div className="h-screen w-full bg-stone-100 overflow-hidden font-sans relative" data-id="element-168">
      <main className="h-full flex flex-col overflow-y-auto" data-id="element-169">
        {state.status === 'idle' && <StartScreen queueLength={state.queue.length} onStart={handleStart} data-id="element-170" />}

        {state.status === 'active' && state.currentLoan && <div className="flex-1 flex items-center justify-center p-8" data-id="element-171">
            <AnimatePresence mode="wait" data-id="element-172">
              <ActiveLoanCard key={state.currentLoan.id} loan={state.currentLoan} onActionClick={handleActionClick} historyCount={state.history.length} onHistoryClick={() => setHistoryOpen(true)} data-id="element-173" />
            </AnimatePresence>
          </div>}

        {state.status === 'complete' && <QueueEmptyState history={state.history} data-id="element-174" />}
      </main>

      {/* Modals */}
      <CallDispositionModal isOpen={state.currentAction === 'calling'} loan={state.currentLoan} onClose={handleCloseModal} onSubmit={handleCallSubmit} data-id="element-175" />

      <NoteModal isOpen={state.currentAction === 'noting'} loan={state.currentLoan} onClose={handleCloseModal} onSubmit={handleNoteSubmit} data-id="element-176" />

      <SnoozeModal isOpen={state.currentAction === 'snoozing'} loan={state.currentLoan} onClose={handleCloseModal} onSubmit={handleSnoozeSubmit} data-id="element-177" />

      {/* History Modal */}
      <HistorySidebar history={state.history} isOpen={historyOpen} onClose={() => setHistoryOpen(false)} data-id="element-178" />
    </div>;
}